var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCallback, useEffect, useReducer, useRef } from "react";
import { useCurrent } from "./useCurrent";
/**
 * Just like `useAsync`, but defaults `immediate` to `false` i.e. not invoked on mount, you need to explicitly call the
 * function yourself (e.g. via submit handler or button click).
 *
 * @template T
 * @param {FetchFunction | UseAsyncConfig} loaderOrConfig
 * @param {Array<*>?} deps
 * @returns {UseAsyncResult<T>}
 **/
export function useAsyncUpdate(loaderOrConfig, deps = []) {
    const config = makeConfig(loaderOrConfig, { immediate: false });
    return useAsync(config, deps);
}
/**
 * @example
 * // Basic example, loading static resource once, on mount
 * const { data: timeMelb, loading, error } = useAsync(() => API_service.api_promise('timeMelb'));

 * @example
 * // Loading resource whenever some prop changes
 * const { data: scr_obj, loading, error } = useAsync(() => sid && API_service.api_promise(`script/${sid}`), [sid]);

 * @example
 * // Loading based on user input with option to retry in case of error
 * const [id, setId] = useState(null);
 * const { loading, error, data, fn: load } = useAsync(() => id && API_service.api_promise('movie/${id}'), [id]);
 * return (<>
 *     <select onChange={({target:{value}}) => setId(value)}>
 *         <option>--select--</option>
 *         {someListOfOptions().map(
 *            opt => <option key={opt.id} value={opt.id}>{opt.label}</option>
 *         )}
 *     </select>
 *     {id && <>
 *         {loading && <>loading...</>}
 *         {data && <pre>{JSON.stringify(data)}</pre>}
 *         {error && <>That didn't work, <button onClick={load}>try again?</button></>}
 *     </>}
 * <>)
 */
export function useAsync(loaderOrConfig, deps = []) {
    const _cfg = makeConfig(loaderOrConfig);
    const initial = { loading: Boolean(_cfg.immediate), seq: 0 };
    const [state, dispatch] = useReducer(loaderReducer, null, () => initial);
    const config = useCurrent(_cfg);
    const sequence = useRef(0);
    const abortController = useRef(null);
    const callApi = useCallback((...args) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (typeof config.current.fn === "function") {
            const seq = ++sequence.current;
            dispatch(Object.assign({ seq, loading: true }, (config.current.keep ? {} : { data: undefined })));
            try {
                (_a = abortController.current) === null || _a === void 0 ? void 0 : _a.abort();
                abortController.current = new AbortController();
                const fn = config.current.cancellable
                    ? config.current.fn.bind({ signal: abortController.current.signal })
                    : config.current.fn;
                const data = yield fn(...args);
                dispatch({ seq, data });
                return data;
            }
            catch (error) {
                dispatch({ seq, error });
                return Promise.reject(error);
            }
        }
    }), deps);
    useEffect(() => {
        if (config.current.immediate) {
            callApi().catch(() => {
                // ignore
            });
        }
    }, [_cfg.immediate, ...deps]);
    useEffect(() => {
        return () => {
            var _a;
            (_a = abortController.current) === null || _a === void 0 ? void 0 : _a.abort();
        };
    }, []);
    return Object.assign(Object.assign({}, state), { fn: callApi });
}
function loaderReducer(state, _a) {
    var { seq } = _a, action = __rest(_a, ["seq"]);
    if ("loading" in action) {
        return Object.assign(Object.assign(Object.assign({}, state), action), { seq, error: undefined });
    }
    if (seq < state.seq) {
        // completion action is stale, keep more recent state
        return state;
    }
    if ("error" in action) {
        return {
            seq,
            loading: false,
            error: action.error,
        };
    }
    if ("data" in action) {
        return {
            seq,
            loading: false,
            data: action.data,
        };
    }
    return state;
}
/**
 * @param {FetchFunction | UseAsyncConfig} loaderOrConfig
 * @param {Partial<UseAsyncConfig>} overrides
 * @return {UseAsyncConfig}
 **/
function makeConfig(loaderOrConfig, overrides = {}) {
    return Object.assign(Object.assign({ immediate: true, keep: true, cancellable: false }, overrides), (typeof loaderOrConfig === "function" ? { fn: loaderOrConfig } : loaderOrConfig));
}
